import axios from "axios";
import { getActiveBranchId } from "../utils/branchStorage";

// ✅ CORREGIDO: Usar variable de entorno para URL de API (crítico para producción)
// En desarrollo: REACT_APP_API_URL=http://localhost:8000/api
// En producción: REACT_APP_API_URL=https://tu-dominio.com/api
const API_BASE_URL = "http://137.184.152.245:8000/api/";

// Mantener DEVURL para compatibilidad con código existente
export const DEVURL = API_BASE_URL;

// Configuración de retry para requests fallidos
const RETRY_DELAY = 1000;

export const Axios = axios.create({
  baseURL: API_BASE_URL,
  timeout: 15000, // Aumentado a 15 segundos para operaciones pesadas
});

// Cache de requests para evitar duplicados
const pendingRequests = new Map();

// Generar key única para request
const getRequestKey = (config) => {
  return `${config.method}-${config.url}-${JSON.stringify(config.params || {})}`;
};

// Interceptor para peticiones (token injection & logging)
Axios.interceptors.request.use(
  (config) => {
    // Check if we should skip auth injection
    if (config.skipAuth) {
      return config;
    }

    // Deduplicación de requests (evitar doble envío)
    if (config.deduplicate !== false) {
      const key = getRequestKey(config);
      if (pendingRequests.has(key)) {
        return pendingRequests.get(key);
      }

      const promise = Promise.resolve(config);
      pendingRequests.set(key, promise);

      // Limpiar después de completar
      setTimeout(() => pendingRequests.delete(key), 100);
    }

    // Logging básico solo en desarrollo y endpoints no críticos
    const isDev =
      typeof process !== "undefined" && process.env.NODE_ENV !== "production";
    const url = config.url || "";
    const suppressLogEndpoints = [
      "branches/my-default-branch-theme/",
      "branches/my-branches",
      "permissions/my-permissions",
    ];
    const shouldSuppress = suppressLogEndpoints.some((e) => url?.includes(e));
    if (isDev && !shouldSuppress && config.method?.toUpperCase() !== "GET") {
      // eslint-disable-next-line no-console
      console.log(`[API] ${config.method?.toUpperCase()} ${url}`);
    }

    // Inyectar token automáticamente si existe
    const token = localStorage.getItem("token");
    if (token) {
      config.headers.Authorization = `Token ${token}`;

      // ✅ OPTIMIZADO: Usar helper centralizado para branch_id
      const activeBranchId = getActiveBranchId();
      if (activeBranchId) {
        config.headers["X-Branch-ID"] = String(activeBranchId);
      }
    }

    return config;
  },
  (error) => {
    return Promise.reject(error);
  },
);

// Interceptor para respuestas (manejo global de errores optimizado)
Axios.interceptors.response.use(
  (response) => {
    return response;
  },
  async (error) => {
    const originalRequest = error.config;

    // Manejar diferentes tipos de errores de manera más robusta
    if (error.response) {
      const { status, data } = error.response;

      // Si es error 401 (no autorizado)
      if (status === 401) {
        // Solo limpiar si no estamos en la página de login
        if (window.location.pathname !== "/login") {
          // Mostrar mensaje antes de redirigir (mejor UX)
          const shouldRedirect = window.confirm(
            "Tu sesión ha expirado. ¿Deseas ir a la página de inicio de sesión?",
          );

          if (shouldRedirect) {
            localStorage.removeItem("token");
            localStorage.removeItem("user");
            localStorage.removeItem("activeBranchId");
            sessionStorage.removeItem("activeBranchId");
            window.location.href = "/login?session_expired=true";
          }
        }
        return Promise.reject(error);
      }

      // Retry automático para errores de red (502, 503, 504)
      if ([502, 503, 504].includes(status) && !originalRequest._retry) {
        originalRequest._retry = true;
        await new Promise((resolve) => setTimeout(resolve, RETRY_DELAY));
        return Axios(originalRequest);
      }

      // Errores silenciados en producción
      const isDev = process.env.NODE_ENV !== "production";

      // Si es error 403 (prohibido)
      if (status === 403 && isDev) {
        // eslint-disable-next-line no-console
        console.warn(
          "[API] Acceso prohibido:",
          data?.detail || data?.error || "No tiene permisos para esta acción",
        );
      }

      // Si es error 404 (no encontrado) - solo loggear endpoints importantes
      if (status === 404 && isDev) {
        const url = originalRequest?.url || "";
        const suppress404Endpoints = [
          "my-default-branch-theme",
          "my-branches-select",
          "theme-config",
          "my-permissions",
        ];
        const shouldSuppress = suppress404Endpoints.some((e) =>
          url.includes(e),
        );
        if (!shouldSuppress) {
          // eslint-disable-next-line no-console
          console.warn("[API] Recurso no encontrado:", url);
        }
      }

      // Si es error 500 (error interno del servidor)
      if (status === 500 && isDev) {
        // eslint-disable-next-line no-console
        console.error(
          "[API] Error interno del servidor:",
          data?.detail || data?.error || "Contacte al administrador",
        );
      }
    } else if (error.request) {
      // La petición fue hecha pero no se recibió respuesta (timeout)
      if (
        !originalRequest._retry &&
        originalRequest.method?.toUpperCase() === "GET"
      ) {
        originalRequest._retry = true;
        await new Promise((resolve) => setTimeout(resolve, RETRY_DELAY));
        return Axios(originalRequest);
      }

      if (process.env.NODE_ENV !== "production") {
        // eslint-disable-next-line no-console
        console.warn("[API] Timeout - No se recibió respuesta del servidor");
      }
    } else if (process.env.NODE_ENV !== "production") {
      // Algo pasó al configurar la petición
      // eslint-disable-next-line no-console
      console.error("[API] Error de configuración:", error.message);
    }

    // Propagar el error para que los componentes puedan manejarlo
    return Promise.reject(error);
  },
);

export const POST_LOGIN = async (endpoint, data) => {
  try {
    const request = await Axios.post(endpoint, data);
    return request;
  } catch (error) {
    throw error;
  }
};

export const GET = async (endpoint, config = {}) => {
  try {
    const request = await Axios.get(endpoint, config);
    return request;
  } catch (error) {
    throw error;
  }
};

export const POST = async (endpoint, data) => {
  try {
    const request = await Axios.post(endpoint, data);
    return request;
  } catch (error) {
    throw error;
  }
};

export const PUT = async (endpoint, data) => {
  try {
    const request = await Axios.put(endpoint, data);
    return request;
  } catch (error) {
    throw error;
  }
};

export const POST_FORM = async (endpoints, data) => {
  try {
    const formdata = new FormData();
    for (const key in data) {
      // Solo agregar si el valor no es null o undefined
      if (data[key] !== null && data[key] !== undefined) {
        formdata.append(key, data[key]);
      }
    }
    // No necesitamos establecer headers manualmente, el interceptor pone el token
    // y axios/navagador manejan el content-type multipart
    const request = await Axios.post(endpoints, formdata);
    return request;
  } catch (error) {
    throw error;
  }
};

export const PATCH = async (endpoint, data) => {
  try {
    const request = await Axios.patch(endpoint, data);
    return request;
  } catch (error) {
    throw error;
  }
};

export const PATCH_FORM = async (endpoints, data) => {
  try {
    const formdata = new FormData();
    for (const key in data) {
      // Solo agregar si el valor no es null o undefined
      if (data[key] !== null && data[key] !== undefined) {
        formdata.append(key, data[key]);
      }
    }

    const request = await Axios.patch(endpoints, formdata);
    return request;
  } catch (error) {
    throw error;
  }
};

export const DELETE = async (endpoint) => {
  try {
    const request = await Axios.delete(endpoint);
    return request;
  } catch (error) {
    throw error;
  }
};

export const forgot_password = async (data) => {
  try {
    // No envía token, solo el email (POST_LOGIN ya usa axios instance que inyectará token si existe,
    // pero para forgot password usualmente no hay token, lo cual es correcto)
    const response = await POST_LOGIN("auth/users/forgot_password/", data);
    return response;
  } catch (error) {
    throw error;
  }
};

export const PING = async () => {
  // Construye la URL base del backend, pero sin el /api/
  const base = DEVURL.replace(/\/api\/?$/, "");
  return axios.get(`${base}/ping/`);
};

// Función para obtener la URL de la API
export const getApiUrl = () => DEVURL;

// Función para cancelar requests pendientes (útil al cambiar de página)
export const cancelPendingRequests = () => {
  pendingRequests.clear();
};

// Configurar timeout por request
export const setRequestTimeout = (timeoutMs) => {
  Axios.defaults.timeout = timeoutMs;
};
